import type { DataType } from "@repo/lanes";

import type { OperationType } from "../operation-types";

/**
 * Operations whose result is always a count-style integer, independent of the
 * source column's type (e.g. counting a STRING or DATE column yields a number).
 * Their output is dimensionless — it never inherits the source column's format
 * (a COUNT of a currency column is a plain count, not a currency value).
 */
export const COUNT_OUTPUT_OPS = new Set<OperationType>([
  "COUNT",
  "COUNT_VALUE",
  "COUNT_EMPTY",
  "COUNT_FALSE",
  "COUNT_TRUE",
  "COUNT_UNIQUE",
]);

/**
 * Operations whose result is always numeric regardless of the source column's
 * type. Covers the count family, the percentage family, and the numeric
 * reducers (`SUM`, `MEAN`, `MEDIAN`, `VARIANCE`).
 *
 * `MIN` / `MAX` are intentionally excluded — they preserve the input type (MIN
 * of dates is a date, MIN of strings is a string). See `TYPE_PRESERVING_OPS`.
 */
export const NUMERIC_OUTPUT_OPS = new Set<OperationType>([
  ...COUNT_OUTPUT_OPS,
  "PERCENTAGE_EMPTY",
  "PERCENTAGE_FALSE",
  "PERCENTAGE_NON_EMPTY",
  "PERCENTAGE_TRUE",
  "SUM",
  "MEAN",
  "MEDIAN",
  "VARIANCE",
]);

/**
 * Operations whose result preserves the source column's type (and therefore its
 * format): `MIN` / `MAX` on a date return a date, on a string return a string,
 * on a number return a number.
 */
export const TYPE_PRESERVING_OPS = new Set<OperationType>(["MIN", "MAX"]);

/** Returns true when the operation's output is always numeric. */
export const isNumericOperation = (op: OperationType): boolean =>
  NUMERIC_OUTPUT_OPS.has(op);

/**
 * The canonical, single source of truth for the data type a column has AFTER an
 * aggregation operation is applied to it.
 *
 *   - Numeric ops (counts, percentages, SUM/MEAN/MEDIAN/VARIANCE) -> `NUMBER`
 *   - `CONCAT` -> `STRING` (STRING_AGG always produces text)
 *   - `MIN` / `MAX` -> the input type (preserved)
 *
 * Consumers (chart axis eligibility, chart SQL transforms, table footers, the
 * Block Agent's column projection) all derive their numeric/non-numeric
 * decisions from this function so they stay consistent when a user changes an
 * operation (e.g. COUNT -> CONCAT flips a column back to text everywhere).
 */
export const operationOutputType = (
  op: OperationType,
  inputType: DataType,
): DataType => {
  if (NUMERIC_OUTPUT_OPS.has(op)) return "NUMBER";
  if (op === "CONCAT") return "STRING";
  // MIN / MAX preserve the source column's type.
  return inputType;
};
